import { forEach, omit, lowerCase, split, chain, capitalize, get, concat, isArray, map, isUndefined, join, keyBy, some, size, includes, isNil, toLower, isFunction } from "lodash"
import { getFactorShortLabel, getContentSetLabel } from "helpers/factors"
import { formatCurrency } from "helpers/formatters/numbers"
import { formatLabel, compareId } from "helpers/securities"
import { BETA_FACTORS } from "helpers/analyzeConstants"
import { titleCase } from "helpers/string"
import { LONG_SHORT } from "../filters"
export function noValueFormatter(metric, operator) {
  return `${metric} ${operator}`
}

export function singleFormatter(metric, operator, dataType, value, currency) {
  let suffix = ""
  let displayValue = value
  switch (dataType) {
    case "percent":
      suffix = "%"
      break
    case "millions":
      displayValue = formatCurrency(value, currency)
  }
  return `${metric} ${operator} ${displayValue + suffix}`
}

export function rangeFormatter(metric, operator, dataType, values, currency) {
  let suffix = ""
  let min = values[0]
  let max = values[1]
  switch (dataType) {
    case "percent":
      suffix = "%"
      break
    case "millions":
      min = formatCurrency(min, currency)
      max = formatCurrency(max, currency)
  }
  return `${metric} ${operator} ${min + suffix} and ${max + suffix}`
}

export function multiFormatter(metric, operator, dataType, values) {
  return `${metric} ${operator} [${join(values, ", ")}]`
}

const STRING_OPERATORS = {
  eq: {
    label: "is",
    formatter: singleFormatter,
  },
  neq: {
    label: "is not",
    formatter: singleFormatter,
  },
  in: {
    label: "is in",
    formatter: multiFormatter,
  },
  notIn: {
    label: "is not in",
    formatter: multiFormatter,
  },
}

const UNIVERSE_OPERATORS = {
  in: {
    label: "in",
  },
  notIn: {
    label: "not in",
  },
}

const NUMERIC_OPERATORS = {
  gt: {
    label: ">",
    formatter: singleFormatter,
  },
  gte: {
    label: ">=",
    formatter: singleFormatter,
  },
  lt: {
    label: "<",
    formatter: singleFormatter,
  },
  lte: {
    label: "<=",
    formatter: singleFormatter,
  },
  eq: {
    label: "=",
    formatter: singleFormatter,
  },
  neq: {
    label: "≠",
    formatter: singleFormatter,
  },
  between: {
    label: "is between",
    formatter: rangeFormatter,
    subFetcher: ({ min, max }) => ([min, max]),
  },
  notBetween: {
    label: "is not between",
    formatter: rangeFormatter,
    subFetcher: ({ min, max }) => ([min, max]),
  },
}

const NUMERIC_AND_BOOL_OPERATORS = {
  ...NUMERIC_OPERATORS,
  hasValue: {
    label: "has value",
    formatter: noValueFormatter,
  },
  hasNoValue: {
    label: "has no value",
    formatter: noValueFormatter,
  },
}

function convertAssetClass(value) {
  if (value === "swap") {
    return "Basket"
  }

  return chain(value)
    .words()
    .map(capitalize)
    .join(" ")
    .value()
}

const PORTFOLIO_FILTERS_FIELD_MAP = {
  country: "countries",
  currency: "currencies",
  assetClass: "assetClasses",
  assetSubclass: "assetSubclasses",
  tag: "tags",
}

const getPortfolioFilterField = (filterType) => PORTFOLIO_FILTERS_FIELD_MAP[filterType] || filterType

const getIsFilterValueAvailable = (portfolioFilters, field, value, minElements = 2) => {
  if (includes(["averageDailyVolume", "marketCapitalization"], field)) {
    // these filter values are always available
    return true
  }

  const portfolioFilterValues = chain(portfolioFilters)
    .get(getPortfolioFilterField(field))
    .map("value")
    .value()

  if (size(portfolioFilterValues) < minElements) {
    return false
  }

  if (isArray(value)) {
    return chain(value)
      .intersection(portfolioFilterValues)
      .size()
      .value() === size(value)
  }

  return some(portfolioFilterValues, filterValue => filterValue === value)
}

function descriptorFetcher(filters, { filter, filterType: { label, dataType, operators, field }, id, portfolioFilters, currency }) {
  const operator = Object.keys(filter)[0]
  const operatorMeta = operators[operator]
  const value = operatorMeta.subFetcher ? operatorMeta.subFetcher(filter[operator]) : filter[operator]
  const isFilterValueAvailable = getIsFilterValueAvailable(portfolioFilters, field, value)

  filters.push({
    label: operatorMeta.formatter(label, operatorMeta.label, dataType, value, currency),
    type: "descriptor",
    id,
    error: portfolioFilters && !isFilterValueAvailable,
  })
}

function assetClassFetcher(filters, { filter, filterType: { label, dataType, operators, field }, id, portfolioFilters }) {
  const operator = Object.keys(filter)[0]
  const operatorMeta = operators[operator]
  let value = filter[operator]
  const isFilterValueAvailable = getIsFilterValueAvailable(portfolioFilters, field, value)

  if (isArray(value)) {
    value = map(value, convertAssetClass)
  } else {
    value = convertAssetClass(value)
  }
  filters.push({
    label: operatorMeta.formatter(label, operatorMeta.label, dataType, value),
    type: "descriptor",
    id,
    error: portfolioFilters && !isFilterValueAvailable,
  })
}

function longShortFetcher(filters, { filter, id }) {
  filters.push({
    label: capitalize(filter),
    type: LONG_SHORT,
    id,
  })
}

function riskFetcher(filters, { filter, filterType: { label, dataType, operators } }) {
  forEach(filter, (riskFilter, index) => {
    const operator = Object.keys(omit(riskFilter, "type"))[0]
    const operatorMeta = operators[operator]
    const value = operatorMeta.subFetcher ? operatorMeta.subFetcher(riskFilter[operator]) : riskFilter[operator]

    filters.push({
      label: operatorMeta.formatter(label[riskFilter.type], operatorMeta.label, dataType, value),
      type: "risk",
      id: index,
    })
  })
}

function exposureFetcher(filters, { filter, filterType: { dataType, operators }, factorsMetadata }) {
  forEach(filter, (exposureFilter, index) => {
    const operator = Object.keys(omit(exposureFilter, "id"))[0]
    const operatorMeta = operators[operator]
    const value = operatorMeta.subFetcher ? operatorMeta.subFetcher(exposureFilter[operator]) : exposureFilter[operator]
    const label = !exposureFilter.contentSetId ? getFactorShortLabel(factorsMetadata, exposureFilter.id) : getContentSetLabel(factorsMetadata, exposureFilter.id, exposureFilter.contentSetId)

    filters.push({
      label: operatorMeta.formatter(`${label} Exposure`, operatorMeta.label, dataType, value),
      type: "factorExposure",
      id: index,
    })
  })
}

function factorSensitivityFetcher(filters, { filter, filterType: { dataType, operators }, factorsMetadata }) {
  forEach(filter, (factorSensitivityFilter, index) => {
    const operator = chain(factorSensitivityFilter)
      .omit(["id", "type"])
      .keys()
      .first()
      .value()
    const operatorMeta = operators[operator]
    const value = isFunction(operatorMeta.subFetcher) ? operatorMeta.subFetcher(factorSensitivityFilter[operator]) : factorSensitivityFilter[operator]
    const label = getFactorShortLabel(factorsMetadata, factorSensitivityFilter.id)

    filters.push({
      label: operatorMeta.formatter(`${label} ${capitalize(factorSensitivityFilter.type)} Factor Sensitivity`, operatorMeta.label, dataType, value),
      type: `${toLower(factorSensitivityFilter.type)}FactorSensitivity`,
      id: index,
    })
  })
}

export function flattenClassificationValues(values) {
  let result = []
  forEach(values, v => {
    result.push({ value: v.id, name: v.name })
    if (v.values) {
      const subLevel = flattenClassificationValues(v.values)
      result = concat(result, subLevel)
    }
  })

  return result
}

function classificationFetcher(filters, { filter: classificationFilters, filterType: { dataType, operators }, classificationsInfos, portfolioFilters }) {
  forEach(classificationFilters, (classificationFilter, index) => {
    const operator = Object.keys(omit(classificationFilter, "id"))[0]
    const operatorMeta = operators[operator]
    let value = classificationFilter[operator]

    const classificationLabel = chain(classificationsInfos)
      .get("classificationsMetas")
      .find(c => c.id === classificationFilter.id)
      .get("name")
      .value()

    const classificationValues = get(classificationsInfos, `classificationsValues.${classificationFilter.id}.version.values`)
    let valuesLabels
    if (classificationValues) {
      valuesLabels = flattenClassificationValues(classificationValues)
    } else {
      valuesLabels = chain(portfolioFilters)
        .get(["classifications", classificationFilter.id])
        .map(({ value, label }) => ({ value, name: label }))
        .value()
    }

    if (!isArray(value)) {
      value = chain(valuesLabels).find(l => l.value === value.value).get("name").value()
      filters.push({
        id: index,
        type: "classification",
        label: value ? operatorMeta.formatter(classificationLabel, operatorMeta.label, dataType, value) : `${classificationFilter.id} ${operatorMeta.label} ${classificationFilter[operator].value}`,
        error: isUndefined(value),
      })
    } else {
      // Array operators
      filters.push({
        id: index,
        type: "classification",
        label: `${classificationFilter.id} ${operatorMeta.label}`,
        valueLabels: map(value, v => {
          const label = chain(valuesLabels).find(l => l.value === v.value).get("name").value()
          return { value: label || v.value, error: !label }
        }),
      })
    }
  })
}

const computeMembershipLabelAndError = (value, { categoryLabel, operatorMeta: { formatter, label: operatorLabel }, dataType, isFilterValueAvailable, filterValue }) => {
  if (isArray(value)) {
    return {
      label: `${categoryLabel} ${operatorLabel}`,
      valueLabels: map(value, v => ({
        value: titleCase(v),
        error: isNil(v) || !isFilterValueAvailable,
      })),
    }
  }
  return {
    label: value
      ? formatter(categoryLabel, operatorLabel, dataType, titleCase(value))
      : `${categoryLabel} ${operatorLabel} ${titleCase(filterValue)}`,
    error: isNil(value) || !isFilterValueAvailable,
  }
}

function membershipFetcher(filters, { filter: membershipFilter, filterType, attributeCategories, tagCategories, portfolioFilters }) {
  const { dataType, operators, field } = filterType
  const categoryIds = keyBy(field === "tag" ? tagCategories : attributeCategories, "id")
  forEach(membershipFilter, ({ id, ...filterFields }, index) => {
    const operator = chain(filterFields).keys().head().value()
    const operatorMeta = operators[operator]
    const categoryLabel = get(categoryIds, [id, "name"])
    const value = filterFields[operator]
    const isFilterValueAvailable = getIsFilterValueAvailable(get(portfolioFilters, getPortfolioFilterField(field), []), id, value, 1)

    filters.push({
      id: index,
      type: field,
      ...computeMembershipLabelAndError(value, { categoryLabel, operatorMeta, dataType, isFilterValueAvailable, filterValue: get(filterFields, [operator, "value"]) }),
    })
  })
}

function universeFetcher(filters, { filter, filterType: { operators } }) {
  forEach(filter, (universe, index) => {
    const operator = Object.keys(omit(universe, "type"))[0]
    const operatorMeta = operators[operator]
    let name = universe[operator]
    if (universe.type === "ETF") {
      const parts = split(universe[operator], /\./g)
      if (parts.length > 1) { // If it's a ticker
        parts.pop() // Remove the MIC
        name = parts.join(".") // Rejoins all the parts
      }
    }

    filters.push({
      label: `${operatorMeta.label} ${lowerCase(universe.type)} ${name}`,
      type: "universe",
      id: index,
    })
  })
}

function securitiesFetcher(filters, { filter, filterType: { operators }, securitiesDescriptors }) {
  forEach(filter, (value, key) => {
    filters.push({
      label: `Securities ${operators[key].label} [${chain(value).map(
        elem => {
          const country = chain(securitiesDescriptors)
            .find(desc => compareId(desc, elem))
            .get("country")
            .value()
          return formatLabel({ ...elem, country })
        }).join(", ").value()}]`,
      type: "securities",
      id: key,
    })
  })
}

function betaFetcher(filters, { filter, filterType: { dataType, operators } }) {
  forEach(filter, (betaFilter, index) => {
    const operator = Object.keys(omit(betaFilter, "id"))[0]
    const operatorMeta = operators[operator]
    const value = operatorMeta.subFetcher ? operatorMeta.subFetcher(betaFilter[operator]) : betaFilter[operator]
    const label = get(BETA_FACTORS, `${index}.label`)
    filters.push({
      label: operatorMeta.formatter(`${label} Beta`, operatorMeta.label, dataType, value),
      type: "beta",
      id: index,
    })
  })
}

export const FILTER_TYPES = [{
  field: "longShort",
  dataType: "constant",
  fetcher: longShortFetcher,
}, {
  field: "universe",
  operators: UNIVERSE_OPERATORS,
  fetcher: universeFetcher,
}, {
  field: "securities",
  operators: UNIVERSE_OPERATORS,
  fetcher: securitiesFetcher,
}, {
  field: "country",
  label: "Country",
  dataType: "string",
  operators: STRING_OPERATORS,
  fetcher: descriptorFetcher,
}, {
  field: "currency",
  label: "Currency",
  dataType: "string",
  operators: STRING_OPERATORS,
  fetcher: descriptorFetcher,
}, {
  field: "assetClass",
  label: "Asset Class",
  dataType: "string",
  operators: STRING_OPERATORS,
  fetcher: assetClassFetcher,
}, {
  field: "assetSubclass",
  label: "Asset Subclass",
  dataType: "string",
  operators: STRING_OPERATORS,
  fetcher: assetClassFetcher,
}, {
  field: "averageDailyVolume",
  label: "Average Daily Volume",
  dataType: "millions",
  operators: NUMERIC_OPERATORS,
  fetcher: descriptorFetcher,
}, {
  field: "marketCapitalization",
  label: "Market Cap",
  dataType: "millions",
  operators: NUMERIC_OPERATORS,
  fetcher: descriptorFetcher,
}, {
  field: "risk",
  label: {
    TOTAL: "Total Risk",
    VAR_DECOMP_SPECIFIC: "Specific Risk",
    VAR_DECOMP_FACTORS: "Factor Risk",
  },
  dataType: "percent",
  operators: NUMERIC_OPERATORS,
  fetcher: riskFetcher,
}, {
  field: "factorExposure",
  dataType: "numeric",
  operators: NUMERIC_AND_BOOL_OPERATORS,
  fetcher: exposureFetcher,
}, {
  field: "factorSensitivity",
  dataType: "numeric",
  operators: NUMERIC_AND_BOOL_OPERATORS,
  fetcher: factorSensitivityFetcher,
}, {
  field: "beta",
  dataType: "numeric",
  operators: NUMERIC_OPERATORS,
  fetcher: betaFetcher,
}, {
  field: "classification",
  label: "Classification",
  dataType: "string",
  operators: STRING_OPERATORS,
  fetcher: classificationFetcher,
}, {
  field: "tag",
  label: "Tags",
  dataType: "string",
  operators: STRING_OPERATORS,
  fetcher: membershipFetcher,
}, {
  field: "positionAttributes",
  label: "Position Attributes",
  dataType: "string",
  operators: STRING_OPERATORS,
  fetcher: membershipFetcher,
}]

export const computeChipSecuritiesIds = filter => chain(filter)
  .get("securities")
  .filter(elem => elem.mic !== "_SWP")
  .map(elem => map(elem, security => `${security.ticker}.${security.mic}`))
  .flatten()
  .value()

export function computeFilterChips(filter, tagCategories, classificationsMetas, securitiesDescriptors, portfolioFilters, attributeCategories, classificationsValues, currency) {
  const filters = []

  if (filter) {
    forEach(FILTER_TYPES, filterType => {
      const { field } = filterType
      if (filter[field] && filter[field].length !== 0) {
        filterType.fetcher(filters, {
          filter: filter[field],
          filterType,
          id: field,
          securitiesDescriptors,
          classificationsInfos: { classificationsMetas, classificationsValues },
          tagCategories,
          portfolioFilters,
          attributeCategories,
          currency,
        })
      }
    })
  }

  return filters
}
